"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useEffect, useRef, useState } from "react";
import { Linkedin } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const experts = [{
  name: "Anjali Verma",
  role: "Business Development Manager",
  expertise: "EU-India Trade Relations",
  bio: "Specializes in bridging European and Indian business opportunities, with over 10 years of experience.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577832/img/team_anjali_verma.png",
  linkedinUrl: "#"
}, {
  name: "Liam Carter",
  role: "Regulatory Affairs Specialist",
  expertise: "International Trade Law",
  bio: "Expert in international trade laws and compliance, ensuring smooth cross-border transactions.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577832/img/team_liam_carter.png",
  linkedinUrl: "#"
}];
export default function ComplianceExperts() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="compliance-experts" className="bg-background py-20 md:py-32 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div className="mx-auto max-w-3xl text-center mb-16">
          <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl mb-4">
            Our Compliance Experts
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Meet the dedicated professionals at EuroIndiaTrade.com who ensure your
            cross-border operations are seamless, compliant, and optimized for
            success in both European and Indian markets.
          </p>
        </div>

        <div ref={ref} className={`mx-auto grid max-w-5xl grid-cols-1 gap-8 md:grid-cols-2 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {experts.map((expert, i) => <Card key={i} data-index={i} className="flex flex-col border-border bg-card shadow-sm transition-all duration-300 hover:shadow-md hover:border-primary/20 group">
              <CardHeader className="flex flex-col items-center text-center pb-4 pt-8">
                <SafeHydrate><Avatar className="size-28 mb-6 border-4 border-background shadow-sm group-hover:scale-105 transition-transform duration-300">
                  <AvatarImage src={expert.image} alt={expert.name} />
                  <AvatarFallback className="text-2xl font-semibold bg-primary/10 text-primary">
                    {expert.name.split(" ").map(n => n[0]).join("")}
                  </AvatarFallback>
                </Avatar></SafeHydrate>
                <CardTitle className="text-2xl font-bold text-card-foreground mb-1">
                  {expert.name}
                </CardTitle>
                <CardDescription className="text-primary font-medium text-sm uppercase tracking-wider">
                  {expert.expertise}
                </CardDescription>
              </CardHeader>
              <CardContent className="text-center flex-grow">
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {expert.bio}
                </p>
              </CardContent>
              <CardFooter className="justify-center pb-8 pt-0">
                <Button variant="ghost" size="icon" className="rounded-full text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors" asChild>
                  <Link to={expert.linkedinUrl} newTab aria-label={`Connect with ${expert.name} on LinkedIn`}>
                    <Linkedin className="size-5" />
                  </Link>
                </Button>
              </CardFooter>
            </Card>)}
        </div>
      </div>
    </section>;
}